import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { keep, kept } from "./keep.js";

/**
 * Petits hooks partagés par les écrans : ce qui se répétait d'une feuille à l'autre (curseurs qui n'envoient
 * qu'à l'arrêt du doigt, écouteurs posés une fois) avec, à chaque fois, le même piège évité — une fonction
 * capturée au départ puis appelée plus tard, quand l'écran a déjà changé.
 */

/**
 * La dernière valeur (prop, fonction, état) lue depuis un écouteur ou un minuteur posé UNE fois : l'écouteur ne se
 * repose pas à chaque rendu, et il n'appelle jamais une fonction d'un rendu passé.
 */
export function useLatest<T>(value: T): RefObject<T> {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

/**
 * Un gestionnaire à l'identité stable qui appelle toujours la DERNIÈRE fonction reçue : un enfant mémorisé
 * (React.memo) ne se redessine pas parce que l'écran parent a recréé ses fonctions en se redessinant.
 */
export function useEvent<A extends unknown[], R>(fn: (...args: A) => R): (...args: A) => R {
  const latest = useLatest(fn);
  return useCallback((...args: A) => latest.current(...args), [latest]);
}

/**
 * Un curseur qui n'envoie sa valeur que quand le doigt s'arrête (`ms` sans changement) : pas une version par cran.
 * La valeur locale suit le doigt ; `send` est toujours la DERNIÈRE fonction reçue (la feuille rouverte sur une
 * autre version n'envoie pas sur l'ancienne) ; la feuille fermée avant l'arrêt, plus rien ne part.
 */
export function useSettled<T>(value: T, send: (v: T) => void, ms = 500): [T, (v: T) => void] {
  const [local, setLocal] = useState(value);
  const latest = useLatest(send);
  const timer = useRef(0);
  useEffect(() => setLocal(value), [value]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const set = useCallback(
    (v: T) => {
      setLocal(v);
      clearTimeout(timer.current);
      timer.current = window.setTimeout(() => latest.current(v), ms);
    },
    [latest, ms],
  );
  return [local, set];
}

// ——— Les feuilles ouvertes, empilées ———————————————————————————————————————

/**
 * Les feuilles ouvertes, de la plus ancienne à celle du dessus. Chaque feuille écoute Échap sur le document ;
 * sans cette pile, une seule touche fermait d'un coup une feuille ET la fenêtre ouverte par-dessus.
 */
const openLayers: symbol[] = [];

/** Une feuille s'ouvre : elle passe au-dessus des autres. */
export function enterLayer(token: symbol) {
  leaveLayer(token);
  openLayers.push(token);
}
/** Une feuille se ferme : elle quitte la pile, où qu'elle soit. */
export function leaveLayer(token: symbol) {
  const i = openLayers.indexOf(token);
  if (i >= 0) openLayers.splice(i, 1);
}
/** Seule la feuille du dessus répond à Échap. */
export function isTopLayer(token: symbol): boolean {
  return openLayers[openLayers.length - 1] === token;
}
/** Une feuille, le temps qu'elle est montée : son jeton, au-dessus des autres. */
export function useLayer(): RefObject<symbol> {
  const token = useRef(Symbol("feuille"));
  useEffect(() => {
    const me = token.current;
    enterLayer(me);
    return () => leaveLayer(me);
  }, []);
  return token;
}

// ——— Données, adresse, écran, appui long ——————————————————————————————————

/**
 * Recharge une donnée asynchrone ; `reload` la relance, l'erreur est gardée pour l'afficher.
 * Avec `keepKey`, l'écran repart de ce qu'il montrait la dernière fois (shared/keep.ts) et se met
 * à jour quand la réponse arrive : `loading` reste faux, `refreshing` dit qu'une mise à jour court.
 */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[], keepKey?: string | null) {
  const [state, setState] = useState<{ data?: T; error?: unknown; loading: boolean; refreshing?: boolean }>(() => {
    const data = keepKey ? kept<T>(keepKey) : undefined;
    return data === undefined ? { loading: true } : { data, loading: false, refreshing: true };
  });
  const [n, setN] = useState(0);
  useEffect(() => {
    // Un drapeau par lancement : la réponse d'un lancement dépassé (autre espace, autre projet) n'écrase
    // ni l'écran ni ce qui est gardé.
    let active = true;
    const prior = keepKey ? kept<T>(keepKey) : undefined;
    setState((s) => (prior !== undefined ? { data: prior, loading: false, refreshing: true } : { ...s, loading: true, refreshing: false, error: undefined }));
    load().then(
      (data) => {
        if (!active) return;
        if (keepKey) keep(keepKey, data);
        setState({ data, loading: false });
      },
      // Une mise à jour ratée laisse ce qui était montré ; sans rien à montrer, l'erreur s'affiche.
      (error) => active && setState((s) => (s.data !== undefined && prior !== undefined ? { data: s.data, loading: false } : { error, loading: false })),
    );
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n, keepKey]);
  return {
    ...state,
    reload: () => setN((x) => x + 1),
    set: (data: T) => {
      if (keepKey) keep(keepKey, data);
      setState({ data, loading: false });
    },
  };
}

export function useQuery(): URLSearchParams {
  return new URLSearchParams(location.search);
}

/** Vrai sur un écran large (le rail à gauche, les panneaux côte à côte). */
export function useWide(min = 1024): boolean {
  const q = `(min-width: ${min}px)`;
  const [wide, setWide] = useState(() => typeof matchMedia === "function" && matchMedia(q).matches);
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const m = matchMedia(q);
    const on = () => setWide(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, [q]);
  return wide;
}

/** Un appui long (menu d'un projet sur téléphone) : 500 ms sans bouger. */
export function useLongPress(onLong: () => void, ms = 500) {
  const timer = useRef<number | null>(null);
  const fired = useRef(false);
  const clear = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };
  return {
    handlers: {
      onPointerDown: () => {
        fired.current = false;
        clear();
        timer.current = window.setTimeout(() => {
          fired.current = true;
          onLong();
        }, ms);
      },
      onPointerUp: clear,
      onPointerLeave: clear,
      onPointerMove: (e: React.PointerEvent) => (Math.abs(e.movementX) + Math.abs(e.movementY) > 6 ? clear() : undefined),
      onContextMenu: (e: React.MouseEvent) => {
        e.preventDefault();
        clear();
        if (!fired.current) onLong();
      },
    },
    /** À appeler dans onClick : un appui long n'est pas aussi un tap. */
    consumed: () => {
      const f = fired.current;
      fired.current = false;
      return f;
    },
  };
}
